import { createTheme } from '@mui/material/styles';

/**
 * Organization Theme Generator
 * Creates dynamic themes based on organization branding settings
 */
export class OrganizationThemeGenerator {
  static createDynamicTheme(orgSettings = {}) {
    const {
      primaryColor = '#1976d2',
      secondaryColor = '#dc004e',
      backgroundColor = '#f5f5f5',
      primaryFontFamily = 'Roboto',
      secondaryFontFamily = 'Roboto',
      // Legacy support
      fontFamily = 'Roboto',
      logo = '',
      title = 'Organization Dashboard',
      customCss = ''
    } = orgSettings;

    const normalizedPrimaryColor = (typeof primaryColor === 'string' && primaryColor.trim()) ? primaryColor : '#1976d2';
    const normalizedSecondaryColor = (typeof secondaryColor === 'string' && secondaryColor.trim()) ? secondaryColor : '#dc004e';
    const normalizedBackgroundColor = (typeof backgroundColor === 'string' && backgroundColor.trim()) ? backgroundColor : '#f5f5f5';

    // Base theme configuration
    const theme = createTheme({
      palette: {
        primary: {
          main: normalizedPrimaryColor,
          light: this.lightenColor(normalizedPrimaryColor, 0.2),
          dark: this.darkenColor(normalizedPrimaryColor, 0.2)
        },
        secondary: {
          main: normalizedSecondaryColor,
          light: this.lightenColor(normalizedSecondaryColor, 0.2),
          dark: this.darkenColor(normalizedSecondaryColor, 0.2)
        },
        background: {
          default: normalizedBackgroundColor,
          paper: '#ffffff'
        },
        text: {
          primary: this.getContrastColor(normalizedBackgroundColor),
          secondary: this.getContrastColor(normalizedBackgroundColor, 0.7)
        }
      },
      typography: {
        // Default body font
        fontFamily: `${secondaryFontFamily || fontFamily}, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif`,
        // Headings use primary font
        h1: { fontFamily: `${primaryFontFamily || fontFamily}, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif` },
        h2: { fontFamily: `${primaryFontFamily || fontFamily}, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif` },
        h3: { fontFamily: `${primaryFontFamily || fontFamily}, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif` },
        h4: { fontFamily: `${primaryFontFamily || fontFamily}, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif` },
        h5: { fontFamily: `${primaryFontFamily || fontFamily}, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif` },
        h6: { fontFamily: `${primaryFontFamily || fontFamily}, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif` },
        // Body text uses secondary font
        body1: { fontFamily: `${secondaryFontFamily || fontFamily}, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif` },
        body2: { fontFamily: `${secondaryFontFamily || fontFamily}, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif` },
        caption: { fontFamily: `${secondaryFontFamily || fontFamily}, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif` }
      },
      components: {
        MuiAppBar: {
          styleOverrides: {
            root: {
              backgroundColor: normalizedPrimaryColor,
              backgroundImage: 'none'
            }
          }
        },
        MuiButton: {
          styleOverrides: {
            root: {
              textTransform: 'none',
              borderRadius: 8
            }
          }
        },
        MuiCard: {
          styleOverrides: {
            root: {
              borderRadius: 12,
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
            }
          }
        }
      }
    });

    return theme;
  }

  /**
   * Apply organization-specific CSS variables to the document
   */
  static applyOrganizationCssVariables(orgSettings) {
    const {
      primaryColor = '#1976d2',
      secondaryColor = '#dc004e',
      backgroundColor = '#f5f5f5',
      primaryFontFamily = 'Roboto',
      secondaryFontFamily = 'Roboto',
      // Legacy support
      fontFamily = 'Roboto'
    } = orgSettings;

    const root = document.documentElement;
    
    // Set organization-specific CSS variables
    root.style.setProperty('--org-primary-color', primaryColor);
    root.style.setProperty('--org-secondary-color', secondaryColor);
    root.style.setProperty('--org-background-color', backgroundColor);
    root.style.setProperty('--org-primary-light', this.lightenColor(primaryColor, 0.1));
    root.style.setProperty('--org-primary-dark', this.darkenColor(primaryColor, 0.1));
    root.style.setProperty('--org-contrast-text', this.getContrastColor(primaryColor));

    // CRITICAL: Apply to main theme variables that dashboard components use
    root.style.setProperty('--color-primary', primaryColor);
    root.style.setProperty('--color-secondary', secondaryColor);
    root.style.setProperty('--color-background', backgroundColor);
    
    // Apply font family variables
    const primaryFont = primaryFontFamily || fontFamily;
    const secondaryFont = secondaryFontFamily || fontFamily;
    
    const primaryFontStack = `${primaryFont}, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif`;
    const secondaryFontStack = `${secondaryFont}, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif`;
    
    // Primary font for headings
    root.style.setProperty('--font-family-heading', primaryFontStack);
    root.style.setProperty('--org-primary-font-family', primaryFont);
    
    // Secondary font for body text
    root.style.setProperty('--font-family-base', secondaryFontStack);
    root.style.setProperty('--font-family-body', secondaryFontStack);
    root.style.setProperty('--org-secondary-font-family', secondaryFont);
    
    // Legacy support
    root.style.setProperty('--org-font-family', primaryFont);
    
    // Apply primary color variations with transparency
    const primaryRgb = this.hexToRgb(primaryColor);
    const secondaryRgb = this.hexToRgb(secondaryColor);
    
    if (primaryRgb) {
      const { r, g, b } = primaryRgb;
      root.style.setProperty('--color-primary-rgb', `${r}, ${g}, ${b}`);
      root.style.setProperty('--color-primary-01', `rgba(${r}, ${g}, ${b}, 0.01)`);
      root.style.setProperty('--color-primary-02', `rgba(${r}, ${g}, ${b}, 0.02)`);
      root.style.setProperty('--color-primary-03', `rgba(${r}, ${g}, ${b}, 0.03)`);
      root.style.setProperty('--color-primary-05', `rgba(${r}, ${g}, ${b}, 0.05)`);
      root.style.setProperty('--color-primary-06', `rgba(${r}, ${g}, ${b}, 0.06)`);
      root.style.setProperty('--color-primary-08', `rgba(${r}, ${g}, ${b}, 0.08)`);
      root.style.setProperty('--color-primary-10', `rgba(${r}, ${g}, ${b}, 0.1)`);
      root.style.setProperty('--color-primary-12', `rgba(${r}, ${g}, ${b}, 0.12)`);
      root.style.setProperty('--color-primary-15', `rgba(${r}, ${g}, ${b}, 0.15)`);
      root.style.setProperty('--color-primary-20', `rgba(${r}, ${g}, ${b}, 0.2)`);
      root.style.setProperty('--color-primary-80', `rgba(${r}, ${g}, ${b}, 0.8)`);
    }
    
    if (secondaryRgb) {
      const { r, g, b } = secondaryRgb;
      root.style.setProperty('--color-secondary-03', `rgba(${r}, ${g}, ${b}, 0.03)`);
      root.style.setProperty('--color-secondary-05', `rgba(${r}, ${g}, ${b}, 0.05)`);
    }
  }

  /**
   * Apply custom CSS for organization
   */
  static applyCustomCSS(customCss, organizationId) {
    // Remove existing organization styles
    const existingStyle = document.getElementById(`org-styles-${organizationId}`);
    if (existingStyle) {
      existingStyle.remove();
    }

    // Apply new styles if provided
    if (customCss) {
      const styleElement = document.createElement('style');
      styleElement.id = `org-styles-${organizationId}`;
      styleElement.textContent = `
        /* Organization Custom Styles */
        .organization-dashboard {
          ${customCss}
        }
      `;
      document.head.appendChild(styleElement);
    }
  }

  /**
   * Create a complete organization theme configuration
   */
  static createOrganizationTheme(organizationData) {
    const orgSettings = {
      primaryColor: organizationData?.branding?.primaryColor || '#1976d2',
      secondaryColor: organizationData?.branding?.secondaryColor || '#dc004e',
      backgroundColor: organizationData?.branding?.backgroundColor || '#f5f5f5',
      fontFamily: organizationData?.branding?.fontFamily || 'Roboto',
      logo: organizationData?.branding?.logo || '',
      title: organizationData?.name || 'Organization Dashboard',
      customCss: organizationData?.branding?.customCss || ''
    };

    // Create the theme
    const theme = this.createDynamicTheme(orgSettings);

    // Apply CSS variables
    this.applyOrganizationCssVariables(orgSettings);

    // Apply custom CSS
    if (orgSettings.customCss) {
      this.applyCustomCSS(orgSettings.customCss, organizationData?.id);
    }

    return {
      theme,
      orgSettings,
      organizationData
    };
  }

  /**
   * Generate organization iframe theme parameters
   */
  static generateIframeThemeParams(orgSettings) {
    const params = new URLSearchParams();
    
    // Always include organization colors if they exist, even if they match defaults
    if (orgSettings.primaryColor) {
      params.append('primaryColor', orgSettings.primaryColor);
    }
    if (orgSettings.secondaryColor) {
      params.append('secondaryColor', orgSettings.secondaryColor);
    }
    if (orgSettings.fontFamily && orgSettings.fontFamily !== 'Roboto') {
      params.append('fontFamily', orgSettings.fontFamily);
    }
    if (orgSettings.backgroundColor && orgSettings.backgroundColor !== '#f5f5f5') {
      params.append('backgroundColor', orgSettings.backgroundColor);
    }

    return params.toString() ? `&${params.toString()}` : '';
  }

  // Utility functions
  static hexToRgb(hex) {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : null;
  }

  static rgbToHex(r, g, b) {
    return "#" + [r, g, b].map(x => {
      const hex = x.toString(16);
      return hex.length === 1 ? "0" + hex : hex;
    }).join("");
  }

  static lightenColor(hex, amount) {
    const rgb = this.hexToRgb(hex);
    if (!rgb) return hex;
    
    const { r, g, b } = rgb;
    const newR = Math.min(255, Math.round(r + (255 - r) * amount));
    const newG = Math.min(255, Math.round(g + (255 - g) * amount));
    const newB = Math.min(255, Math.round(b + (255 - b) * amount));
    
    return this.rgbToHex(newR, newG, newB);
  }

  static darkenColor(hex, amount) {
    const rgb = this.hexToRgb(hex);
    if (!rgb) return hex;
    
    const { r, g, b } = rgb;
    const newR = Math.max(0, Math.round(r * (1 - amount)));
    const newG = Math.max(0, Math.round(g * (1 - amount)));
    const newB = Math.max(0, Math.round(b * (1 - amount)));
    
    return this.rgbToHex(newR, newG, newB);
  }

  static getContrastColor(backgroundColor, opacity = 1) {
    const rgb = this.hexToRgb(backgroundColor);
    if (!rgb) return '#000000';
    
    const { r, g, b } = rgb;
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    
    const textColor = luminance > 0.5 ? '#000000' : '#ffffff';
    return opacity < 1 ? `rgba(${luminance > 0.5 ? '0,0,0' : '255,255,255'}, ${opacity})` : textColor;
  }
}

/**
 * Organization Theme Presets
 * Based on the original dashboard themes with updated names
 */
export const organizationThemePresets = {
  'situation-room-default': {
    primaryColor: '#005F94',
    secondaryColor: '#006D87',
    backgroundColor: '#F6F8FA',
    fontFamily: 'Open Sans',
    name: 'Default'
  },
  'un-blue': {
    primaryColor: '#0072BC',
    secondaryColor: '#8EBEFF',
    backgroundColor: '#F6F8FA',
    fontFamily: 'Lato',
    name: 'UN Blue'
  },
  'humanitarian-red': {
    primaryColor: '#E2001A',
    secondaryColor: '#011e4a',
    backgroundColor: '#F6F8FA',
    fontFamily: 'Open Sans',
    name: 'Humanitarian Red'
  },
  'dark-red': {
    primaryColor: '#A72824',
    secondaryColor: '#006D87',
    backgroundColor: '#F6F8FA',
    fontFamily: 'Barlow',
    name: 'Dark Red'
  }
};

/**
 * Organization Theme Manager
 * Manages theme application and persistence for organizations
 */
export class OrganizationThemeManager {
  constructor() {
    this.currentOrgTheme = null;
    this.originalTheme = null;
  }

  /**
   * Apply organization theme
   */
  applyTheme(organizationData, themeChangeCallback) {
    try {
      const themeConfig = OrganizationThemeGenerator.createOrganizationTheme(organizationData);
      this.currentOrgTheme = themeConfig;
      
      // Apply the theme via callback if provided
      if (themeChangeCallback && typeof themeChangeCallback === 'function') {
        themeChangeCallback(themeConfig.theme);
      }

      console.log('Organization theme applied:', themeConfig.orgSettings);
      return themeConfig;
    } catch (error) {
      console.error('Failed to apply organization theme:', error);
      return null;
    }
  }

  /**
   * Reset to default theme
   */
  resetTheme(themeChangeCallback) {
    try {
      // Remove organization-specific styles
      const orgStyles = document.querySelectorAll('[id^="org-styles-"]');
      orgStyles.forEach(style => style.remove());

      // Reset CSS variables to defaults
      const root = document.documentElement;
      root.style.removeProperty('--org-primary-color');
      root.style.removeProperty('--org-secondary-color');
      root.style.removeProperty('--org-background-color');
      root.style.removeProperty('--org-primary-light');
      root.style.removeProperty('--org-primary-dark');
      root.style.removeProperty('--org-contrast-text');

      // Reset theme via callback
      if (themeChangeCallback && typeof themeChangeCallback === 'function') {
        themeChangeCallback(null); // Reset to default theme
      }

      this.currentOrgTheme = null;
      console.log('Organization theme reset to default');
    } catch (error) {
      console.error('Failed to reset organization theme:', error);
    }
  }

  /**
   * Get current organization theme
   */
  getCurrentTheme() {
    return this.currentOrgTheme;
  }
}

// Export singleton instance
export const organizationThemeManager = new OrganizationThemeManager();

// Export everything
export default {
  OrganizationThemeGenerator,
  organizationThemePresets,
  OrganizationThemeManager,
  organizationThemeManager
}; 